import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setCredentials } from "../../redux/slices/authSlice";
import { fetchUser } from "../../redux/slices/profileSlice";
import { announceUserChange } from "../../utilities/currentUser";
import api from "../../api/axios";
import { FaSpinner, FaTimesCircle } from "react-icons/fa";
import { toast } from "react-toastify";

const SSOCallback = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");

  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const savedState = localStorage.getItem("sso_state");

    // CSRF verification
    if (!state || state !== savedState) {
      setError("Invalid SSO state. Session expired or untrusted request.");
      return;
    }

    if (!code) {
      setError("Authorization code missing from SSO provider.");
      return;
    }

    // Exchange code for local application token
    const exchangeSSOCode = async () => {
      try {
        const response = await api.post("/api/auth/sso-login", { code });
        const { token, user } = response.data;

        if (!token || !user) {
          throw new Error("Invalid SSO response payload.");
        }

        // Save local credentials to Redux and LocalStorage
        dispatch(setCredentials({ user, token }));
        announceUserChange(); // the sidebar reads companies from the stored user
        dispatch(fetchUser());

        // Clear local state
        localStorage.removeItem("sso_state");

        toast.success(`Welcome back, ${user.name}!`);

        const role = user.roles?.[0]?.name?.toLowerCase();
        
        // Dynamic route redirection based on role approval configuration
        if (role && user.company_id) {
          try {
            const resRoles = await api.get(`/api/company/${user.company_id}/role-approval-settings`);
            const activeRoles = resRoles.data.roles
              .filter(r => r.is_active)
              .map(r => r.role_name.toLowerCase());

            if (activeRoles.includes(role)) {
              navigate("/pending-users");
              return;
            }
          } catch (roleErr) {
            console.error("Failed to check active roles approval configuration", roleErr);
          }
        }

        // Default redirect
        navigate("/profile");
      } catch (err) {
        console.error("SSO Exchange Error:", err);
        setError(err.response?.data?.message || err.message || "Failed to exchange SSO credentials.");
      }
    };

    exchangeSSOCode();
  }, [searchParams, navigate, dispatch]);

  return (
    <div className="d-flex align-items-center justify-content-center vh-100 bg-light">
      <div className="card border-0 shadow p-5 text-center" style={{ maxWidth: "450px", borderRadius: "12px" }}>
        {!error ? (
          <div>
            <FaSpinner className="text-primary spinner-border border-0 mb-3" size={40} style={{ animation: "spin 1s linear infinite" }} />
            <h4 className="fw-bold mt-4">Completing Single Sign-On</h4>
            <p className="text-muted">Verifying credentials and logging you in...</p>
          </div>
        ) : (
          <div>
            <FaTimesCircle className="text-danger mb-3" size={50} />
            <h4 className="fw-bold text-dark">SSO Login Failed</h4>
            <p className="text-danger mt-2">{error}</p>
            <button className="btn btn-primary mt-4 px-4 py-2" style={{ borderRadius: "8px", backgroundColor: "#2e86de", borderColor: "#2e86de" }} onClick={() => navigate("/login")}>
              Back to Login
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SSOCallback;
